import React, { useState } from 'react';
import { Button } from 'components/ui/button';
import { Form, Modal } from 'react-bootstrap-5';
import { faFileExport } from '@fortawesome/pro-solid-svg-icons';
import DatePicker from 'react-datepicker';
import { createExport, callListUrl, CALL_LIST_EXPORT, profileDataExportsUrl } from '../../../helpers/prospect_api';
import { type DataExportTemplate } from 'bundles/connect/types';

type Props = {
  callListId: number,
  show: boolean,
  templates: DataExportTemplate[],
  onHide: () => void
}

export default function ActivityExport({ callListId, show, templates, onHide }: Props) {
  const [loading, setLoading] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);
  const [exportName, setExportName] = useState('');
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [templateId, setTemplateId] = useState<string>();
  const [includeCalls, setIncludeCalls] = useState(true);
  const [includeEmails, setIncludeEmails] = useState(true);
  const [includeOther, setIncludeOther] = useState(true);
  const [emailNotification, setEmailNotification] = useState(true);

  const handleExport = () => {
    setLoading(true);
    const dataExport = {
      name: exportName,
      startDate,
      endDate,
      connectDataExportTemplateId: templateId,
      includeCalls,
      includeEmails,
      includeOther,
      emailNotification
    };
    createExport(callListUrl(CALL_LIST_EXPORT, callListId), { type: 'activity', dataExport }).then(() => {
      setLoading(false);
      setExportComplete(true);
    });
  };

  if (!show) return null;

  return (
    <>
      <Modal.Body>
        {exportComplete ? (
          <div>
            Activity export queued!&nbsp;
            Check your inbox or navigate to your <a href={profileDataExportsUrl}>Profile Settings</a>&nbsp;
            to download your export
          </div>
        ) : (
          <>
            <Form.Group className="mb-4">
              <Form.Label>Name</Form.Label>
              <Form.Control
                placeholder="Export Name"
                value={exportName}
                onChange={e => setExportName(e.target.value)}
              />
            </Form.Group>
            <h6 className="mb-1 fw-bold">Export Options</h6>
            <Form.Group className="d-flex align-items-center gap-2 mb-2">
              <div className="flex-fill">
                <Form.Label className="mb-0">From</Form.Label>
                <div>
                  <DatePicker
                    className="form-control"
                    dateFormat="Pp"
                    maxDate={endDate}
                    selected={startDate}
                    onChange={setStartDate}
                  />
                </div>
              </div>
              <div className="flex-fill">
                <Form.Label className="mb-0">To</Form.Label>
                <div>
                  <DatePicker
                    className="form-control"
                    dateFormat="Pp"
                    minDate={startDate}
                    selected={endDate}
                    onChange={setEndDate}
                  />
                </div>
              </div>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Type of Activity</Form.Label>
              <Form.Check
                checked={includeCalls}
                className="d-flex gap-1 align-items-center"
                id="export-phone-calls-check"
                label="Export Phone Calls"
                type="checkbox"
                onChange={e => setIncludeCalls(e.target.checked)}
              />
              <Form.Check
                checked={includeEmails}
                className="d-flex gap-1 align-items-center"
                id="export-emails-check"
                label="Export Emails"
                type="checkbox"
                onChange={e => setIncludeEmails(e.target.checked)}
              />
              <Form.Check
                checked={includeOther}
                className="d-flex gap-1 align-items-center"
                id="export-other-check"
                label="Export Other Activities"
                type="checkbox"
                onChange={e => setIncludeOther(e.target.checked)}
              />
            </Form.Group>
            {templates.length > 0 && (
              <Form.Group className="mb-2">
                <Form.Label>Export Template</Form.Label>
                <Form.Select value={templateId} onChange={e => setTemplateId(e.target.value)}>
                  <option value="">Select a template</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group>
              <Form.Check
                checked={emailNotification}
                className="d-flex gap-1 align-items-center"
                id="email-notification-check"
                label="Email me when the export completes"
                type="checkbox"
                onChange={e => setEmailNotification(e.target.checked)}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {exportComplete ? (
          <Button label="Done!" onClick={onHide} />
        ) : (
          <>
            <Button disabled={loading} label="Cancel" variant="ghost" onClick={onHide} />
            <Button disabled={loading} icon={faFileExport} label="Begin Export" onClick={handleExport} />
          </>
        )}
      </Modal.Footer>
    </>
  );
}
